/**
 * Button — el botón del design system.
 *
 * ⚠️ **Convive con `components/Button.tsx`, que usan 35 archivos.** El namespace
 * `ds/` es justamente lo que permite tenerlos a los dos mientras se migra: un
 * import dice de qué sistema viene cada uno.
 *
 * Qué aporta sobre el del repo:
 *
 *   - las seis variantes salen de `--gradient-brand` y de las familias de
 *     estado. 🔴 El del repo pinta 4 de sus 9 variantes con `bg-red-600`,
 *     `bg-emerald-600`, `bg-amber-600` y `bg-violet-600`: colores crudos que
 *     **no cambian de tono en oscuro ni siguen el branding del tenant**. Es la
 *     deuda #234, y es lo que este port viene a cerrar;
 *   - la altura sale de `--control-h*`, así que en un teléfono los tres tamaños
 *     suben solos al mínimo de toque;
 *   - `loading` pone `aria-busy` y desactiva el botón, que es lo que evita el
 *     doble envío. El del repo muestra el spinner y deja el botón vivo.
 *
 * `danger` es SUTIL (fondo suave, sólido al pasar el mouse): sirve para una
 * acción destructiva entre otras. `*-solid` es la acción que se CONFIRMA en un
 * diálogo —«Eliminar», «Aprobar»—, con relleno sólido y texto blanco, que es
 * lo que el producto ya usa en sus 55 confirmaciones (#332, 2026-09-29).
 *
 * ⚠️ `dashed` no tiene equivalente en el repo: es el botón de «agregar una
 * línea más» de un formulario de documento, que hoy se escribe a mano.
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-solid'
  | 'success-solid'
  | 'warning-solid'
  | 'dashed'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'style'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Desactiva el botón y anuncia `aria-busy`. */
  loading?: boolean
  icon?: ReactNode
  iconRight?: ReactNode
  fullWidth?: boolean
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconRight,
  fullWidth = false,
  children,
  className = '',
  style,
  type = 'button',
  ...rest
}: ButtonProps) {
  const clases = [
    'cl-btn',
    `cl-btn--${variant}`,
    size !== 'md' ? `cl-btn--${size}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={clases}
      // Desactivarlo mientras carga es lo que impide el doble envío. Un botón
      // que muestra el spinner y sigue aceptando clics emite dos documentos.
      disabled={disabled || loading}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      style={{ width: fullWidth ? '100%' : undefined, ...style }}
      {...rest}
    >
      {loading ? <span className="cl-spinner" aria-hidden="true" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  )
}
